import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ResetIcon from '@/assets/icons/reset.svg?react'
import { Loading, message, Modal, Trans } from '@/components'
import { Copy } from '@/components/base/Copy'
import { showCanvasError } from '@/utils/canvasHttp'

import { fetchRouteApiAccess, rotateCredential } from '../api'
import { ModelRoute } from '../types'
import { ApiAccessExample } from './ApiAccessExample'
import { ApiKeyModal } from './ApiKeyModal'
import { SectionLabel } from './SectionLabel'

interface RouteApiAccessProps {
  route: ModelRoute
}

// Keyed by the display labels api.ts AUTH_MODE_LABELS produces (modelRoute ns).
const AUTH_MODE_LABEL_KEYS: Record<string, string> = {
  'Access Token': 'detail.apiAccess.accessToken',
  'HMAC (AK/SK)': 'detail.apiAccess.authModeHmac',
  'Not configured': 'detail.apiAccess.authModeUnsafe',
}

export function RouteApiAccess({ route }: RouteApiAccessProps) {
  const { t } = useTranslation('modelRoute')
  const [rotating, setRotating] = useState(false)
  const [rotatedKey, setRotatedKey] = useState<string | null>(null)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['routeApiAccess', route.resID],
    queryFn: () => fetchRouteApiAccess(route.resID),
  })

  if (isLoading || !data) {
    return (
      <div className="bg-panel-gray-100 rounded-lg flex items-center justify-center p-8">
        <Loading />
      </div>
    )
  }

  const onRotate = async () => {
    if (rotating) return
    setRotating(true)
    try {
      const { apiKey } = await rotateCredential(route.resID)
      message.success(t('detail.apiAccess.rotated', 'Credential rotated'))
      // One-time display: the new key can never be shown again after this modal closes
      setRotatedKey(apiKey)
      refetch()
    } catch (error) {
      showCanvasError(error)
    } finally {
      setRotating(false)
    }
  }

  const onRotateClick = () => {
    Modal.confirm({
      title: (
        <Trans ns="modelRoute" i18nKey="detail.apiAccess.rotateCredential">
          Rotate Credential
        </Trans>
      ),
      content: (
        <span className="t-sm text-secondary">
          <Trans
            ns="modelRoute"
            i18nKey="detail.apiAccess.rotateConfirm"
            components={[<span key="0" className="text-error" />]}
          >
            {
              'A new credential will be generated for this model route. The current credential will <0>stop working immediately.</0>'
            }
          </Trans>
        </span>
      ),
      confirmBtnCfg: {
        type: 'danger',
        text: (
          <Trans ns="modelRoute" i18nKey="detail.apiAccess.rotateCredential">
            Rotate Credential
          </Trans>
        ),
      },
    })
      .then(onRotate)
      .catch(() => {})
  }

  return (
    <div className="bg-panel-gray-100 rounded-lg p-4 flex flex-col gap-4 t-sm text-tertiary">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <div className="t-xs text-secondary">
            <Trans ns="modelRoute" i18nKey="detail.apiAccess.authMode">
              Auth Mode
            </Trans>
          </div>
          <div className="t-sb text-primary">
            {AUTH_MODE_LABEL_KEYS[data.authMode]
              ? t(AUTH_MODE_LABEL_KEYS[data.authMode], data.authMode)
              : data.authMode}
          </div>
        </div>
        <div className="space-y-1">
          <div className="t-xs text-secondary">
            <Trans ns="modelRoute" i18nKey="detail.apiAccess.accessMode">
              API Access Mode
            </Trans>
          </div>
          <div className="t-sb text-primary">{data.accessModes.join(', ')}</div>
        </div>
      </div>

      <SectionLabel>
        <Trans ns="modelRoute" i18nKey="detail.apiAccess.accessInfo">
          Access Info
        </Trans>
      </SectionLabel>

      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans ns="modelRoute" i18nKey="detail.apiAccess.accessUrl">
            Access URL
          </Trans>
        </div>
        <div className="flex items-center gap-2">
          <span className="t-sb text-primary break-all">{data.accessUrl}</span>
          <Copy text={data.accessUrl} size="md" />
        </div>
      </div>

      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans ns="modelRoute" i18nKey="detail.apiAccess.accessExample">
            API Access Example
          </Trans>
        </div>
        <ApiAccessExample
          url={data.example.url}
          model={data.example.model}
          protocol={data.example.protocol}
        />
      </div>

      <SectionLabel>
        <Trans ns="modelRoute" i18nKey="detail.apiAccess.credential">
          Credential
        </Trans>
      </SectionLabel>

      <div className="space-y-1">
        <div className="t-xs text-secondary">
          <Trans ns="modelRoute" i18nKey="detail.apiAccess.accessToken">
            Access Token
          </Trans>
        </div>
        <div className={data.credentialEnabled ? 't-sb text-success' : 't-sb text-error'}>
          {data.credentialEnabled
            ? t('detail.apiAccess.statusActive', 'Active')
            : t('detail.apiAccess.statusRevoked', 'Revoked')}
        </div>
        <p className="t-xs text-tertiary">
          {t(
            'detail.apiAccess.oneTimeNote',
            'API keys are shown only once — at creation or rotation.',
          )}
        </p>
      </div>

      <div
        className="flex items-center gap-1 text-highlight cursor-pointer t-sb w-fit"
        onClick={onRotateClick}
      >
        <ResetIcon className="w-4 h-4" />
        <Trans ns="modelRoute" i18nKey="detail.apiAccess.rotateCredential">
          Rotate Credential
        </Trans>
      </div>

      {rotatedKey && <ApiKeyModal apiKey={rotatedKey} onClose={() => setRotatedKey(null)} />}
    </div>
  )
}
